<template>
  <b-modal
    :active.sync="isModalActive"
    :width="720"
    scroll="keep"
    :can-cancel="canCancel"
    @close="closeModal"
  >
    <div class="border border-blue-light rounded shadow bg-white p-4">
      <form class="w-full" @submit.prevent="save">
        <header class="font-thin uppercase text-blue-light modal-card-head_ mb-4">
          {{ modalTitle }}
        </header>

        <div class="modal-card-body relative">
          <div v-if="isTakeoffTree && !isEditMode" class="w-full flex flex-row mb-3">
            <b-field :label="$t('boq_child_type')" class="w-full">
              <b-radio v-model="form.childType" native-value="discipline">
                {{ $t('discipline') }}
              </b-radio>
              <b-radio v-model="form.childType" native-value="stage" class="ml-4">
                {{ $t('stage') }}
              </b-radio>
            </b-field>
          </div>

          <div v-else-if="isManualTree && !isEditMode" class="w-full flex flex-row mb-3">
            <b-field :label="$t('boq_child_type')" class="w-full">
              <b-radio v-model="form.childType" native-value="certifier_head">
                {{ $t('chapter') }}
              </b-radio>
              <b-radio v-model="form.childType" native-value="resource" class="ml-4">
                {{ $t('resource') }}
              </b-radio>
            </b-field>
          </div>

          <div class="w-full flex flex-row mb-3">
            <b-field :label="$t('code')" class="w-1/2 pr-2">
              <b-input v-model="form.code" required maxlength="255" />
            </b-field>
            <b-field :label="$t('name')" class="w-1/2 pl-2">
              <b-input v-model="form.name" required maxlength="255" />
            </b-field>
          </div>

          <div v-if="showTakeoffTypeField" class="w-full flex flex-row mb-3">
            <b-field :label="$t('takeoff_type')" class="w-full">
              <b-radio v-model="form.takeoff_type" :native-value="1">
                Type 1
              </b-radio>
              <b-radio v-model="form.takeoff_type" :native-value="2" class="ml-4">
                Type 2
              </b-radio>
            </b-field>
          </div>

          <template v-if="isResourceChild">
            <div class="w-full flex flex-row mb-3">
              <b-field :label="$t('select_unit')" class="w-1/2 pr-2">
                <b-select
                  v-model="form.measure_unit_id"
                  expanded
                  :placeholder="$t('select')"
                  :loading="loadingUnits"
                >
                  <option
                    v-for="unit in units"
                    :key="unit.id"
                    :value="unit.id"
                  >
                    {{ unit.name }}
                  </option>
                </b-select>
              </b-field>
              <b-field :label="$t('variant_type')" class="w-1/2 pl-2">
                <b-select v-model="form.variant_type" expanded>
                  <option
                    v-for="option in resourceVariantTypes"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </b-select>
              </b-field>
            </div>

            <div class="w-full flex flex-row mb-3">
              <b-field :label="$t('total_quantity')" class="w-1/2 pr-2">
                <b-input v-model="form.total_quantity" type="number" step="0.01" />
              </b-field>
              <b-field :label="$t('unit_price')" class="w-1/2 pl-2">
                <b-input v-model="form.unit_price" type="number" step="0.01" min="0" />
              </b-field>
            </div>
          </template>

          <b-loading :is-full-page="false" v-model="processingData" :can-cancel="false" />
        </div>

        <footer class="modal-card-foot_">
          <div class="w-full inline-flex justify-end gap-2">
            <v-button type="button" @click="closeModal">
              {{ $t('cancel') }}
            </v-button>
            <button class="btn btn-blue" type="submit" :disabled="processingData">
              {{ $t('save') }}
            </button>
          </div>
        </footer>
      </form>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

const TYPE_CERTIFIER_HEAD = 'App\\Models\\AssignmentType\\CertifierHead'
const TYPE_RESOURCE = 'App\\Models\\AssignmentType\\Resource'

/** Mismos valores que BoqRoot::ROW_KINDS */
const ROW_KIND_TAKEOFF = 0
const ROW_KIND_DISCIPLINE = 1
const ROW_KIND_STAGE = 2

/** Mismos valores que Assignment::VARIANT_TYPES (type1–type4) */
const RESOURCE_VARIANT_TYPES = [
  { value: 10, label: 'Type 1' },
  { value: 11, label: 'Type 2' },
  { value: 12, label: 'Type 3' },
  { value: 13, label: 'Type 4' },
]

export default {
  name: 'boq-create-resource-modal',

  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      isModalActive: false,
      processingData: false,
      loadingUnits: false,
      canCancel: ['escape', 'x', 'outside'],
      parentBoqRootId: null,
      editBoqRootId: null,
      isManualTree: false,
      isTakeoffTree: false,
      parentRowKind: null,
      units: [],
      form: {
        childType: 'resource',
        code: '',
        name: '',
        measure_unit_id: null,
        variant_type: 10,
        total_quantity: null,
        unit_price: null,
        takeoff_type: 1,
      },
      resourceVariantTypes: RESOURCE_VARIANT_TYPES,
    }
  },

  computed: {
    ...mapGetters({
      company: 'app/company',
    }),

    isEditMode() {
      return this.editBoqRootId != null
    },

    isResourceChild() {
      if (this.isTakeoffTree) {
        return false
      }
      return !this.isManualTree || this.form.childType === 'resource'
    },

    /** Solo disciplina hija directa del takeoff (crear o editar). */
    showTakeoffTypeField() {
      if (!this.isTakeoffTree || this.form.childType !== 'discipline') {
        return false
      }
      return this.parentRowKind === ROW_KIND_TAKEOFF
    },

    modalTitle() {
      if (this.isTakeoffTree) {
        return this.isEditMode
          ? this.$t('edit_boq_takeoff_child')
          : this.$t('add_boq_takeoff_child')
      }
      return this.isManualTree
        ? this.$t('add_boq_child')
        : this.$t('add_boq_resource')
    },
  },

  methods: {
    show(parentBoqRoot, options = {}) {
      this.isManualTree = options.isManualTree === true
      this.isTakeoffTree = options.isTakeoffTree === true
      this.parentRowKind = options.parentRowKind != null ? Number(options.parentRowKind) : null

      if (options.edit === true) {
        this.editBoqRootId = parentBoqRoot.id
        this.parentBoqRootId = parentBoqRoot.parent_id || null
        this.resetForm()
        this.populateFromNode(parentBoqRoot, options)
      } else {
        this.editBoqRootId = null
        this.parentBoqRootId = parentBoqRoot.id
        this.resetForm()
      }

      this.isModalActive = true
      if (!this.isTakeoffTree) {
        this.loadUnits()
      }
    },

    populateFromNode(node, options = {}) {
      const rowKind = node.row_kind != null ? Number(node.row_kind) : null
      this.form.childType = rowKind === ROW_KIND_STAGE ? 'stage' : 'discipline'
      this.form.code = node.code || ''
      this.form.name = node.name || ''
      const takeoffType = node.takeoff_type != null
        ? Number(node.takeoff_type)
        : 1
      this.form.takeoff_type = takeoffType === 2 ? 2 : 1

      if (options.parentRowKind != null) {
        this.parentRowKind = Number(options.parentRowKind)
      }
    },

    closeModal() {
      this.isModalActive = false
      this.parentBoqRootId = null
      this.editBoqRootId = null
      this.isManualTree = false
      this.isTakeoffTree = false
      this.parentRowKind = null
      this.resetForm()
    },

    resetForm() {
      this.form = {
        childType: this.isTakeoffTree ? 'discipline' : 'resource',
        code: '',
        name: '',
        measure_unit_id: null,
        variant_type: 10,
        total_quantity: null,
        unit_price: null,
        takeoff_type: 1,
      }
      this.processingData = false
    },

    async loadUnits() {
      if (!this.company || !this.company.id) {
        return
      }
      this.loadingUnits = true
      try {
        const { data } = await axios.get(
          `/api/v2/company/${this.company.id}/measurement-units`,
          { params: { per_page: 300 } }
        )
        this.units = data.data || data.units || []
      } catch (error) {
        this.units = []
      } finally {
        this.loadingUnits = false
      }
    },

    buildPayload() {
      if (this.isEditMode && this.isTakeoffTree) {
        const payload = {
          code: this.form.code,
          name: this.form.name,
        }
        if (this.showTakeoffTypeField) {
          payload.takeoff_type = this.form.takeoff_type
        }
        return payload
      }

      const payload = {
        parent_id: this.parentBoqRootId,
        code: this.form.code,
        name: this.form.name,
      }

      if (this.isTakeoffTree) {
        payload.row_kind = this.form.childType === 'stage'
          ? ROW_KIND_STAGE
          : ROW_KIND_DISCIPLINE
        if (this.showTakeoffTypeField) {
          payload.takeoff_type = this.form.takeoff_type
        }
        return payload
      }

      if (this.isManualTree && this.form.childType === 'certifier_head') {
        payload.type = TYPE_CERTIFIER_HEAD
        payload.variant_type = 1
        return payload
      }

      payload.type = TYPE_RESOURCE
      payload.measure_unit_id = this.form.measure_unit_id
      payload.variant_type = this.form.variant_type
      payload.total_quantity = this.form.total_quantity
      payload.unit_price = this.form.unit_price

      return payload
    },

    async save() {
      if (!this.form.code || !this.form.name) {
        this.$notify.error('required_fields')
        return
      }

      this.processingData = true

      try {
        const payload = this.buildPayload()
        const { data } = this.isEditMode
          ? await axios.patch(
            `/api/v2/project/${this.projectId}/boq-roots/${this.editBoqRootId}`,
            payload
          )
          : await axios.post(
            `/api/v2/project/${this.projectId}/boq-roots`,
            payload
          )

        if (data && data.success) {
          this.$notify.success('save_successful')
          this.$emit(this.isEditMode ? 'updated' : 'created', data.boq_root)
          this.closeModal()
          return
        }

        const msg = data.message || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message)
          || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } finally {
        this.processingData = false
      }
    },
  },
}
</script>
